import React, { useContext } from "react";
import { InlineMath } from "react-katex";
import SvgMathLabel from "./SvgMathLabel";
import { ThemeContext } from "../../context/ThemeContext";

/**
 * Diagram graniastosłupa prawidłowego trójkątnego z przekątną AE.
 * angleDeg ∈ {30, 45, 60}
 *
 * baseHeightLatex – latexowy zapis wysokości podstawy h_Δ (np. "2\\sqrt{3}", "3\\sqrt{3}").
 *
 * Wersja:
 * - stały viewBox dla wszystkich wariantów kąta,
 * - geometria zmienia proporcje, ale nie skaluje typografii,
 * - stabilne grubości linii i rozmiary opisów.
 */
export default function StereoTriangularPrismAngleDiagram({
  angleDeg = 60,
  baseHeightLatex = "2\\sqrt{3}",
}) {
  const { theme } = useContext(ThemeContext);
  const stroke = theme === "light" ? "#000000" : "#ffffff";
  const highlight = "#b37bd8";
  const baseHighlight = "#f5b04d";

  // ===== geometria w układzie współrzędnych SVG =====
  const centerX = 130;

  // bazowa wysokość ściany dla 30°
  const baseRectHeight = 90;

  let heightScale;
  if (angleDeg === 30) heightScale = 1;
  else if (angleDeg === 45) heightScale = 1.5;
  else heightScale = 2;

  const rectHeight = baseRectHeight * heightScale;

  const sqrt3 = Math.sqrt(3);
  let rectWidth;
  if (angleDeg === 45) rectWidth = rectHeight;
  else if (angleDeg === 60) rectWidth = rectHeight / sqrt3;
  else rectWidth = rectHeight * sqrt3;

  const halfW = rectWidth / 2;
  const baseLeftX = centerX - halfW;
  const baseRightX = centerX + halfW;

  // Każdy wariant korzysta z tego samego kadru. Przesuwamy tylko bryłę
  // w pionie, aby 30°, 45° i 60° zajmowały porównywalny obszar.
  const baseYByAngle = { 30: 220, 45: 256, 60: 273 };
  const baseY = baseYByAngle[angleDeg] ?? 256;
  const topY = baseY - rectHeight;

  const A = { x: baseLeftX, y: baseY };
  const B = { x: baseRightX, y: baseY };
  const D = { x: baseLeftX, y: topY };
  const E = { x: baseRightX, y: topY };

  const Cx = centerX;

  const AE_dx = E.x - A.x;
  const AE_dy = E.y - A.y;
  const tOnAE = (Cx - A.x) / AE_dx;
  const yOnAE = A.y + tOnAE * AE_dy;

  const offset = 12 * heightScale * 0.8;
  let Cy;
  if (angleDeg === 60) Cy = yOnAE + offset;
  else Cy = yOnAE - offset;

  const C = { x: Cx, y: Cy };
  const foot = { x: Cx, y: baseY };

  const shiftX = D.x - A.x; // 0
  const shiftY = D.y - A.y; // -rectHeight
  const F = { x: C.x + shiftX, y: C.y + shiftY };

  const angleLabel = `${angleDeg}^{\\circ}`;
  const angleLabelRadius = 30;
  const angleLabelRadians = (angleDeg * Math.PI) / 360;
  const angleLabelX = A.x + angleLabelRadius * Math.cos(angleLabelRadians);
  const angleLabelY = A.y - angleLabelRadius * Math.sin(angleLabelRadians);

  const FixedText = ({
    x,
    y,
    fill,
    fontSize = 12.5,
    children,
    anchor = "start",
    italic = false,
  }) => (
    <text
      x={x}
      y={y}
      fill={fill}
      fontSize={fontSize}
      textAnchor={anchor}
      fontFamily={italic ? "KaTeX_Math, Times New Roman, serif" : "KaTeX_Main, Times New Roman, serif"}
      fontStyle={italic ? "italic" : "normal"}
    >
      {children}
    </text>
  );

  // non-scaling stroke: grubości linii nie rosną/maleją z viewBox
  const ns = { vectorEffect: "non-scaling-stroke" };

  return (
    <div style={{ maxWidth: 380, margin: "0 auto" }}>
        
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <svg
          viewBox="0 0 260 315"
          role="img"
          preserveAspectRatio="xMidYMid meet"
          aria-label="Graniastosłup prawidłowy trójkątny z przekątną AE i kątem między AE i AB"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        >

          {/* ściana boczna ABED */}
          <line {...ns} x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={stroke} strokeWidth="1.6" />
          <line {...ns} x1={A.x} y1={A.y} x2={D.x} y2={D.y} stroke={stroke} strokeWidth="1.6" />
          <line {...ns} x1={B.x} y1={B.y} x2={E.x} y2={E.y} stroke={stroke} strokeWidth="1.6" />
          <line {...ns} x1={D.x} y1={D.y} x2={E.x} y2={E.y} stroke={stroke} strokeWidth="1.6" />

          {/* dach DEF */}
          <line {...ns} x1={D.x} y1={D.y} x2={F.x} y2={F.y} stroke={stroke} strokeWidth="1.6" />
          <line {...ns} x1={E.x} y1={E.y} x2={F.x} y2={F.y} stroke={stroke} strokeWidth="1.6" />

          {/* przekątna AE – fiolet */}
          <line {...ns} x1={A.x} y1={A.y} x2={E.x} y2={E.y} stroke={highlight} strokeWidth="2" />

          {/* AC – przerywana */}
          <line
            {...ns}
            x1={A.x}
            y1={A.y}
            x2={C.x}
            y2={C.y}
            stroke={stroke}
            strokeWidth="1.2"
            strokeDasharray="4 3"
          />

          {/* CB – przerywana */}
          <line
            {...ns}
            x1={C.x}
            y1={C.y}
            x2={B.x}
            y2={B.y}
            stroke={stroke}
            strokeWidth="1.2"
            strokeDasharray="4 3"
          />

          {/* CF – pionowa przerywana */}
          <line
            {...ns}
            x1={C.x}
            y1={C.y}
            x2={F.x}
            y2={F.y}
            stroke={stroke}
            strokeWidth="1.2"
            strokeDasharray="4 3"
          />

          {/* wysokość C -> AB – pomarańczowa */}
          <line
            {...ns}
            x1={C.x}
            y1={C.y}
            x2={foot.x}
            y2={foot.y}
            stroke={baseHighlight}
            strokeWidth="1.3"
            strokeDasharray="4 3"
          />

          {/* kąt prosty zaznaczony łukiem */}
          <path
            {...ns}
            d={`M ${foot.x} ${foot.y - 11} A 11 11 0 0 1 ${foot.x + 11} ${foot.y}`}
            fill="none"
            stroke={baseHighlight}
            strokeWidth="1.4"
          />

          {/* łuk kąta */}
          <path
            {...ns}
            d={describeAngleArc(A.x, A.y, 18, angleDeg)}
            fill="none"
            stroke={highlight}
            strokeWidth="1.5"
          />

          {/* punkty A i E */}
          <circle cx={A.x} cy={A.y} r="2.3" fill={highlight} />
          <circle cx={E.x} cy={E.y} r="2.3" fill={highlight} />

          {/* punkt C */}
          <circle cx={C.x} cy={C.y} r="2" fill={stroke} />

          {/* etykiety (stały rozmiar) */}
          <FixedText x={A.x - 8} y={A.y + 16} fill={stroke} italic>
            A
          </FixedText>
          <FixedText x={B.x + 5} y={B.y + 16} fill={stroke} italic>
            B
          </FixedText>
          <FixedText x={D.x - 9} y={D.y - 7} fill={stroke} italic>
            D
          </FixedText>
          <FixedText x={E.x + 5} y={E.y - 7} fill={stroke} italic>
            E
          </FixedText>
          <FixedText x={F.x} y={F.y - 9} fill={stroke} anchor="middle" italic>
            F
          </FixedText>
          <FixedText x={C.x + 7} y={C.y + 1} fill={stroke} italic>
            C
          </FixedText>

          {/* podpis wysokości (stały rozmiar) */}
          <SvgMathLabel x={foot.x + 7} y={(C.y + foot.y) / 2 + 12} color={baseHighlight} math={baseHeightLatex} fontSize={12.5} width={64} height={28} align="left" />

          {/* podpis kąta (stały rozmiar) */}
          <FixedText
            x={angleLabelX}
            y={angleLabelY + 3}
            fill={highlight}
            anchor="middle"
          >
            {angleDeg}°
          </FixedText>
        </svg>
      </div>

      {/* podpisy pod rysunkiem */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 4,
          fontSize: 13.5,
        }}
      >
        <span>
          <InlineMath math={`h_{\\Delta} = ${baseHeightLatex}`} />
        </span>
        <span>
          <InlineMath math={`\\angle (AE,\\,AB) = ${angleLabel}`} />
        </span>
      </div>
    </div>
  );
}

function describeAngleArc(cx, cy, radius, angleDeg) {
  const startAngle = 0; // wzdłuż AB
  const endAngle = (Math.min(Math.max(angleDeg, 15), 75) * Math.PI) / 180;

  const x1 = cx + radius * Math.cos(startAngle);
  const y1 = cy - radius * Math.sin(startAngle);
  const x2 = cx + radius * Math.cos(endAngle);
  const y2 = cy - radius * Math.sin(endAngle);

  return `M ${x1} ${y1} A ${radius} ${radius} 0 0 0 ${x2} ${y2}`;
}
